import React, { ComponentType, memo, useCallback } from "react";
import { useSelector } from "react-redux";

import { StorageKeys } from "@utils/enums";

import { withWoosMap } from "@services/woosmap/woosmap.initializer";

import { useThunkDispatch } from "@redux/dispatcher";
import {
    clear as clearPreferredStore,
    getPreferredStoreByCode,
} from "@redux/modules/api/data/preferredStore/preferredStore.module";
import { StoreDetailsModel } from "@redux/modules/api/data/stores/@types/storeDetails.model";
import { update as updateProductDetail } from "@redux/modules/value/productDetail/productDetail.module";
import { getProductDetail } from "@redux/modules/value/productDetail/productDetail.selectors";

import { Messages } from "@components/General/Messages/messages.container";

import { StorePickerComponent } from "./storePicker.component";
import { STORE_PICKER_MESSAGE_ID } from "./storePicker.config";
import { StorePickerContextProvider } from "./storePicker.context";
import { StorePickerType } from "./types/storePickerType.enum";

export type StorePickerConfig = {
    storeCode: string | null;
    displayStockLevel: boolean;
    fixedFeatures?: Array<string>;
    fixedFilters?: Array<string>;
    infoLink: string | null;
    onClose?: () => void;
} & (
    | {
          type: StorePickerType.INFO;
      }
    | {
          type: StorePickerType.SELECT;
          submitButtonText?: string;
          onSubmit: (store: StoreDetailsModel, onClose?: () => void) => void;
      }
    | {
          type: StorePickerType.CUSTOM;
          sku: string | null;
          storeCodes: Array<string>;
          SubmitComponent: ComponentType<{}>;
      }
);

type StorePickerProps = { disabled?: boolean } & StorePickerConfig;

export const _StorePicker = ({
    disabled = false,
    ...config
}: StorePickerProps) => {
    const dispatch = useThunkDispatch();
    const productDetail = useSelector(getProductDetail);

    const handleOnSubmit = useCallback(
        (store: StoreDetailsModel) => {
            dispatch(updateProductDetail({ shop: store }));
            dispatch(clearPreferredStore());
            dispatch(
                getPreferredStoreByCode({
                    code: store.code,
                    sku: productDetail.sku,
                }),
            );

            window.sessionStorage.setItem(
                StorageKeys.PreferredStore,
                JSON.stringify({
                    shopId: store.code,
                    shopName: store.storeName,
                }),
            );

            if (config.type === StorePickerType.SELECT) {
                return config.onSubmit(store, config.onClose);
            }

            config.onClose?.();
        },
        [config, dispatch],
    );

    return (
        <>
            <Messages messageid={STORE_PICKER_MESSAGE_ID} />
            <StorePickerContextProvider
                disabled={disabled}
                config={config}
                sku={
                    config.type === StorePickerType.CUSTOM
                        ? config.sku ?? undefined
                        : productDetail.sku
                }
                onSubmit={handleOnSubmit}
            >
                <StorePickerComponent />
            </StorePickerContextProvider>
        </>
    );
};

export const StorePicker = withWoosMap(memo(_StorePicker));
